/**
 * The single icon family for the whole app.
 *
 * Every glyph is drawn on a 24×24 grid with a 1.75 stroke, round caps and round
 * joins, so icons sit together consistently at any size and inherit the
 * surrounding text colour. `<Icon name="calendar" />` is the only entry point —
 * there is no second icon source anywhere in the app, which is what keeps the
 * visual language uniform.
 *
 * Size defaults to 1em so an icon scales with the text it sits beside; pass a
 * number when it needs a fixed box. Decorative by default (aria-hidden); pass a
 * `title` when the icon is the only label for a control.
 */

/* Paths only — the wrapper supplies viewBox, stroke and sizing. */
const PATHS = {
  /* --- Navigation ------------------------------------------------------- */
  home: 'M3 10.5 12 3l9 7.5M5.5 9.5V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9.5M9.5 21v-6h5v6',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
  calendar:
    'M8 2v3M16 2v3M3.5 9.5h17M5 5h14a1.5 1.5 0 0 1 1.5 1.5v13A1.5 1.5 0 0 1 19 21H5a1.5 1.5 0 0 1-1.5-1.5v-13A1.5 1.5 0 0 1 5 5Z',
  clipboard:
    'M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V4ZM9 12h6M9 16h4',
  user: 'M20 21v-1.5A5.5 5.5 0 0 0 14.5 14h-5A5.5 5.5 0 0 0 4 19.5V21M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
  users:
    'M16 21v-1.5a5 5 0 0 0-5-5H6a5 5 0 0 0-5 5V21M8.5 10.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-1.5a5 5 0 0 0-3.75-4.84M15.5 2.66a5 5 0 0 1 0 9.18',
  chart: 'M3 21h18M7 21V11M12 21V4M17 21v-7',
  wallet:
    'M3 8.5A2.5 2.5 0 0 1 5.5 6H19a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5.5A2.5 2.5 0 0 1 3 17.5v-9ZM3 8.5A2.5 2.5 0 0 0 5.5 11H21M16.5 15.5h.01',
  settings:
    'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  megaphone: 'M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1ZM16 8.5a4.5 4.5 0 0 1 0 7M19 5.5a8.5 8.5 0 0 1 0 13',
  toolbox:
    'M3 9.5A1.5 1.5 0 0 1 4.5 8h15A1.5 1.5 0 0 1 21 9.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5v-9ZM8.5 8V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V8M3 13h18',

  /* --- Actions ---------------------------------------------------------- */
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  close: 'M18 6 6 18M6 6l12 12',
  check: 'M20 6 9 17l-5-5',
  'check-circle': 'M22 11.1V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
  'arrow-right': 'M5 12h14M13 5l7 7-7 7',
  'arrow-left': 'M19 12H5M11 19l-7-7 7-7',
  'chevron-right': 'm9 18 6-6-6-6',
  'chevron-left': 'm15 18-6-6 6-6',
  'chevron-down': 'm6 9 6 6 6-6',
  'chevron-up': 'm18 15-6-6-6 6',
  filter: 'M4 5h16M7 12h10M10 19h4',
  sort: 'M4 7h13M4 12h9M4 17h5M17 13l3 3 3-3M20 16V7',
  edit: 'M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z',
  trash: 'M3 6h18M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6M19 6v13.5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5V6M10 11v6M14 11v6',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v13',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  refresh: 'M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16M3 21v-5h5',
  send: 'M21.5 2.5 11 13M21.5 2.5 15 21l-4-8-8-4 18.5-6.5Z',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  external: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  eye: 'M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  camera:
    'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3.5L8.4 3.4A1 1 0 0 1 9.2 3h5.6a1 1 0 0 1 .8.4L17.5 6H21a2 2 0 0 1 2 2v11ZM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',

  /* --- Objects & meta ---------------------------------------------------- */
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3.5 2',
  'map-pin': 'M20 10.5c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0ZM12 13a2.75 2.75 0 1 0 0-5.5 2.75 2.75 0 0 0 0 5.5Z',
  map: 'm9 4-6 2.5v14L9 18l6 2.5 6-2.5v-14L15 6.5 9 4ZM9 4v14M15 6.5v14',
  navigation: 'm3 11 18-8-8 18-2-8-8-2Z',
  phone:
    'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7',
  message: 'M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-4.2-1L3 20.5l1.6-4.8A8.4 8.4 0 0 1 3.6 11.5a8.4 8.4 0 0 1 8.4-8.4h.5a8.4 8.4 0 0 1 8.5 8.4Z',
  bell: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0',
  'bell-off': 'M8.7 3.7A6 6 0 0 1 18 8c0 2 .3 3.5.7 4.7M18.6 18.6H3s3-2 3-9c0-.7.1-1.4.3-2M13.7 21a2 2 0 0 1-3.4 0M2 2l20 20',
  briefcase: 'M4 7h16a1.5 1.5 0 0 1 1.5 1.5v10A1.5 1.5 0 0 1 20 20H4a1.5 1.5 0 0 1-1.5-1.5v-10A1.5 1.5 0 0 1 4 7ZM8.5 7V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V7M2.5 12h19',
  award: 'M12 14a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM8.2 13.1 7 22l5-2.6L17 22l-1.2-8.9',
  shield: 'M12 21s8-3.5 8-9.5V5.5L12 2.5 4 5.5V11.5C4 17.5 12 21 12 21Z',
  'shield-check': 'M12 21s8-3.5 8-9.5V5.5L12 2.5 4 5.5V11.5C4 17.5 12 21 12 21ZM9 11.5l2 2 4-4',
  star: 'm12 2.8 2.9 5.9 6.5.9-4.7 4.6 1.1 6.4-5.8-3-5.8 3 1.1-6.4L2.6 9.6l6.5-.9L12 2.8Z',
  heart: 'M20.8 5.6a5 5 0 0 0-7.1 0L12 7.3l-1.7-1.7a5 5 0 0 0-7.1 7.1l1.7 1.7L12 21.5l7.1-7.1 1.7-1.7a5 5 0 0 0 0-7.1Z',
  stethoscope:
    'M5 3v5a4 4 0 0 0 8 0V3M5 3H3.5M5 3h1.5M13 3h-1.5M13 3h1.5M9 15v1.5a4.5 4.5 0 0 0 9 0V14M18 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  activity: 'M22 12h-4l-3 8-6-16-3 8H2',
  pill: 'M10.5 20.5a5 5 0 0 1-7-7l6-6a5 5 0 0 1 7 7l-6 6ZM7 10l7 7',
  file: 'M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5ZM13.5 2.5V7a1 1 0 0 0 1 1h4.5',
  'file-check': 'M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7l-5-5ZM13.5 2.5V7a1 1 0 0 0 1 1h4.5M9 15l2 2 4-4',
  image: 'M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1ZM8.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM21 15l-5-5L5 21',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z',
  languages: 'M2 5h10M7 3v2c0 4.4-2.2 8-5 8M4 13.5c2.5 0 6-1.5 7-5M13 21l5-11 5 11M15.5 16h5',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 16v-4M12 8h.01',
  warning: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01',
  'x-circle': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM15 9l-6 6M9 9l6 6',
  hourglass: 'M6 2h12M6 22h12M8 2v4.5a4 4 0 0 0 1.6 3.2L12 12l2.4-2.3A4 4 0 0 0 16 6.5V2M8 22v-4.5a4 4 0 0 1 1.6-3.2L12 12l2.4 2.3A4 4 0 0 1 16 17.5V22',
  sparkles: 'm12 3 1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3ZM19 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2Z',
  wifi: 'M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M2 9a15 15 0 0 1 20 0M12 20h.01',
  'wifi-off': 'M2 2l20 20M8.5 16a5 5 0 0 1 6.3-.6M5 12.5a10 10 0 0 1 4-2.4M2 9a15 15 0 0 1 4.6-2.9M12 20h.01M16.7 12.9A10 10 0 0 1 19 12.5M13.3 6.2A15 15 0 0 1 22 9',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z',
  contrast: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 3v18',
  compass: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM15.5 8.5l-2 5-5 2 2-5 5-2Z',
  inbox: 'M21 13h-5l-1.5 3h-5L8 13H3M6.4 4.2h11.2a2 2 0 0 1 1.8 1.1L22 13v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-5l2.6-7.7a2 2 0 0 1 1.8-1.1Z',
  cash: 'M2 7h20a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z M12 15.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM5 10.5h.01M19 13.5h.01',
  repeat: 'M17 2l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
  'list-check': 'M11 6h10M11 12h10M11 18h10M3 6l1.5 1.5L7.5 4.5M3 12l1.5 1.5L7.5 10.5M3 18l1.5 1.5L7.5 16.5',
  key: 'M15.5 3a5.5 5.5 0 1 0-4.9 8L9 12.6 3 18.6V21h3l7.4-7.4a5.5 5.5 0 0 0 2.1-10.6ZM17 7h.01',
  lock: 'M5 11h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1ZM7.5 11V7a4.5 4.5 0 0 1 9 0v4',
  ban: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM5.6 5.6l12.8 12.8',
  flag: 'M4 22V4M4 4h12l-2 4 2 4H4',
};

/** Glyphs that also need a filled shape (star rating, dots). */
const FILLED = new Set(['star-filled']);

export default function Icon({ name, size, className = '', title, strokeWidth = 1.75, style, ...rest }) {
  const filled = name === 'star-filled';
  const d = PATHS[filled ? 'star' : name];

  if (!d) {
    // A missing name should be obvious in development, never a crash in prod.
    if (import.meta.env?.DEV) console.warn(`<Icon> unknown name: "${name}"`);
    return null;
  }

  const dims = size ? { width: size, height: size } : { width: '1em', height: '1em' };

  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={filled ? 1 : strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ ...dims, flex: 'none', ...style }}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : 'true'}
      aria-label={title}
      focusable="false"
      {...rest}
    >
      {title && <title>{title}</title>}
      <path d={d} />
    </svg>
  );
}

export { PATHS as ICON_NAMES, FILLED };
